import { createInstances } from '@echo/api-client/onearly';
import type { StrongerMemoryTypes } from '../config/environment';
import { currentStreak, previousDay } from '../domain/exercise';
import { daysDoneInWeek, weekStartOf } from './group-progress';
import { type SmMark, weekDoneBySelf, withMarks } from './marks';
import { SM_KIND, type SmSummary, WEEK_TARGET } from './schemas';
import { performAsFor, type WriteScope } from './scope';
import { completedDates, type DaySession } from './sessions';
import { shareWithGroup } from './share';

/**
 * The one participant object the group may see (Home › Group progress, "did Bob do today's
 * exercises"): derived from the sessions, written anew after every exercise, never
 * containing answers, timings or photos (PRD-1945 facilitator scope). The app derives it
 * for now; Core's future "Current View" will take over without the screens noticing.
 *
 * Its numbers count the leaders' marks the app could read (`withMarks`), like every count the
 * person sees. `weekDoneBySelf` leaves them out, so the group's view (echo's backend) can add
 * the marks that are done now instead of trusting these.
 */
export function deriveSummary(
  sessions: readonly DaySession[],
  today: string,
  groupActorUuid?: string,
  marks: readonly SmMark[] = [],
): SmSummary {
  const counted = withMarks(sessions, marks);
  const done = completedDates(counted);
  const doneSet = new Set(done);
  let best = 0;
  for (const date of done) {
    if (doneSet.has(previousDay(date))) continue;
    let length = 0;
    let cursor = date;
    while (doneSet.has(cursor)) {
      length += 1;
      cursor = nextDay(cursor);
    }
    best = Math.max(best, length);
  }
  const month = today.slice(0, 7);
  const completionByKind = { read: 0, write: 0, math: 0 };
  for (const session of counted) {
    for (const attempt of session.attempts) {
      if (attempt.value.state !== 'done') continue;
      completionByKind[SM_KIND[attempt.kind]] += 1;
    }
  }
  const weekStart = weekStartOf(today);
  return {
    asOf: today,
    currentStreak: currentStreak(done, today),
    bestStreak: best,
    // Completed days, like the streaks and the total — a half-done day used to count here and
    // nowhere else, so "0 day streak" sat next to "1 this month" (QA note 12).
    sessionsThisMonth: done.filter((date) => date.startsWith(month)).length,
    sessionsTotal: done.length,
    completionByKind,
    weekStart,
    weekTarget: WEEK_TARGET,
    weekDaysDone: daysDoneInWeek(done, weekStart),
    weekDoneBySelf: weekDoneBySelf(sessions, weekStart),
    ...(groupActorUuid ? { groupActorUuid } : {}),
  };
}

/**
 * Writes the summary as a new node, as `scope` says (`scope.ts`), and shares it with the group
 * (`shareWithGroup`): the group itself, which echo's backend reads it as, and its members, whose
 * installed apps read it on Home. The share is made as the same author: Core lets only a node's
 * exact author share it. It is best effort; where it fails the node still exists for its owner.
 * `subjectUuid` is the person it is about, as for their exercises (`RecordAttemptInput`).
 */
export async function writeSummary(
  types: StrongerMemoryTypes,
  summary: SmSummary,
  scope: WriteScope,
  subjectUuid?: string,
): Promise<{ uuid: string; shared: boolean }> {
  const performAs = performAsFor(scope);
  const response = await createInstances({
    nodes: [
      {
        clientId: 'summary',
        contentTypeUuid: types.summary,
        ...(subjectUuid ? { subjectUuid } : {}),
        value: { jsonValue: summary },
      },
    ],
    ...(performAs ? { performAs } : {}),
  });
  if (response.status !== 200)
    throw new Error(`OnEarly: saving the summary failed (${response.status})`);
  const uuid = response.data.created[0]?.uuid;
  if (!uuid) throw new Error('OnEarly: saving the summary returned no node');
  let shared = false;
  if (summary.groupActorUuid) {
    try {
      await shareWithGroup(uuid, summary.groupActorUuid, performAs);
      shared = true;
    } catch {
      // The owner keeps their summary; the group sees it once Core accepts the share.
    }
  }
  return { uuid, shared };
}

function nextDay(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}
